import _ from "lodash";
/** external */
import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { IconButton, Dropdown, DropdownMenu } from "@toddle-edu/ds-web";
import { AddCircleOutlined } from "@toddle-edu/ds-icons";
/** internal */
import { useI18n } from "Hooks";
import { removeAssessmentTool } from "Assessments/modules/AssessmentModule";
import {
  getAssessmentDetailsData,
  replaceLearningGoalsFields,
} from "Worksheet/modules/WorksheetUtils";
import {
  updateActiveQuestionField,
  createQuestionField,
} from "Worksheet/modules/WorksheetModules";
import { scrollIntoView } from "Utils";
import { TODDLE_LIBRARY } from "Constants/stringConstants";
import {
  FIELD_UID_ASSESSMENT_TOOL,
  DYNAMIC_FIELD_DETAILS,
  RESOURCE_TYPE_QUESTION,
  DYNAMIC_FIELDS_V2,
  FIELD_UID_ADD_LEARNING_GOALS,
} from "Worksheet/modules/WorksheetConstants";
import {
  getSelectedAcademicYearId,
  getSelectedCourseId,
} from "modules/CommonSelectors";
import {
  combineUnitPlanMultiFieldData,
  getUnitDetailsFromUnitPlanField,
} from "Assessments/modules/Utils";
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import CustomDropdown from "UnitPlans/components/IBcomponents/CustomDropdown";
import { checkEmptyElementValue } from "modules/ElementValueUtils";
import { getAssessmentLearningGoalsDropdownOptions } from "AppComponents/AssessmentLearningGoals/module/AssessmentLearningGoalsUtils";
import {
  isWorksheetQueriesV2Enabled,
  getIsLearningGoalsRevampEnabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import {
  getQuestionFromCache,
  getQuestionFromCacheV2,
} from "Worksheet/modules/WorksheetGraphqlHelpers";
/** relative */
import DynamicFieldComponent from "./DynamicFieldComponent";

const DynamicFieldsViewV2 = props => {
  const {
    data,
    updateFields,
    isActiveQuestion,
    activeQuestionField,
    questionId,
    assessmentTools,
    removeAssessmentTool,
    mode,
    didMount,
    setDidMount,
    showDynamicFields,
    isSubQuestionSelected,
    updateActiveQuestionField,
    curriculumType,
    isFromInternalTool,
    portalType,
    learningGoalsDropdownOptions,
    selectedCourseId,
    learningGoalsFields,
    assessmentType,
    worksheetTemplateId,
    unitPlanFields,
    unitPlanFieldList,
    createQuestionField,
    learningGoalsMapping,
    getLearningGoalsTagCountTooltip,
    dynamicFieldRefs,
    assessmentId,
    handleLearningGoalsDropdownClick,
    isDynamicFieldDropdown,
    fieldList,
    assessmentFields,
    handleAssessmentToolDropdownClick,
    questionFields,
    isSubQuestion,
    onOptionClick: addNewQuestionField,
    unitPlanTemplate,
  } = props;
  const { t } = useI18n(["common"]);

  const fieldRefs = useRef([]);

  const dynamicFieldDetails = DYNAMIC_FIELD_DETAILS({ curriculumType });

  const isLearningGoalsRevampField = getIsLearningGoalsRevampEnabled();
  const isQueriesV2Enabled = isWorksheetQueriesV2Enabled();

  const setDynamicFieldRef = (fieldUId, fieldRef) => {
    Object.keys(dynamicFieldRefs.current).forEach(key => {
      if (key.endsWith(`-${fieldUId}`)) {
        delete dynamicFieldRefs.current[key];
      }
    });
    dynamicFieldRefs.current[`${questionId}-${fieldUId}`] = fieldRef;
  };

  const addedLearningGoalsFields = _.pickBy(
    learningGoalsFields,
    (fieldObj, fieldKey) =>
      checkEmptyElementValue({
        fieldKey,
        data: { allFields: data },
        unitPlanFields,
        fieldObj,
        ignoreUnitPlanSubjectCheck: true,
        checkOnlyValueKeysForBenchmarks: true,
      })
  );

  const addedLearningGoalsFieldsUid = _.keys(addedLearningGoalsFields);

  useEffect(() => {
    if (
      isActiveQuestion &&
      !_.isNil(activeQuestionField) &&
      !_.isNil(fieldRefs.current[activeQuestionField])
    ) {
      scrollIntoView({
        element: fieldRefs.current[activeQuestionField],
        block: "nearest",
        checkForElementInViewPort: true,
      });

      updateActiveQuestionField(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuestionField]);

  const updateInputField = async args => {
    const { params, isDebounce, extraParams } = args;
    const uid = _.first(_.keys(params));
    let fieldData = _.find(questionFields, { uid });

    if (_.isEmpty(fieldData) && isLearningGoalsRevampField) {
      await addNewQuestionField({ key: uid });
      const questionData = isQueriesV2Enabled
        ? getQuestionFromCacheV2({ id: questionId })
        : getQuestionFromCache({ id: questionId });
      const questionFields = _.concat(
        _.get(questionData, "fields", []),
        _.get(questionData, "answerFields", [])
      );
      fieldData = _.find(questionFields, { uid });
    }

    // id of tool that needs to remove
    const assessmentToolId = _.get(extraParams, "id");

    if (uid === FIELD_UID_ASSESSMENT_TOOL && !_.isEmpty(assessmentToolId)) {
      removeAssessmentTool({
        parentType: "QUESTION",
        parentId: questionId,
        portalType,
        ...extraParams,
      });
    }
    // update existing field
    else if (!_.isEmpty(fieldData)) {
      const fields = [
        {
          id: fieldData?.id,
          value: params[uid],
        },
      ];

      let extraFields = {};
      if (_.includes(_.keys(learningGoalsFields), uid)) {
        extraFields = {
          uid,
          value: params[uid],
          extraParams,
          fieldTemplateObj: learningGoalsFields[uid],
        };
      }

      updateFields({ fields, isDebounce, extraFields });
    }
  };

  const getExtraRightButtonComponent = (uid, options) => {
    switch (uid) {
      case FIELD_UID_ADD_LEARNING_GOALS: {
        // When learning goals revamp is enabled, use the new dropdown options
        const dropdownOptions = isLearningGoalsRevampField
          ? getAssessmentLearningGoalsDropdownOptions({
              t,
              learningGoalsOptions: learningGoalsDropdownOptions,
              curriculumType,
            })
          : learningGoalsDropdownOptions;

        return (
          <Dropdown
            overlay={
              <DropdownMenu
                dsVersion="2.0"
                options={dropdownOptions}
                onClick={handleLearningGoalsDropdownClick}
                testId={"assignmentCreation:addLearningGoals"}
              />
            }
            placement={"bottomRight"}
          >
            <IconButton
              icon={<AddCircleOutlined />}
              dsVersion="2.0"
              type="outlined"
              variant="neutral"
              size={"medium"}
              _legacy_size={"medium"}
              _legacy_variant={"outlined-subtle"}
              testId={"assignmentCreation:addLearningGoals"}
              aria-label={t("common:add_with_label", {
                label: t("common:learning_goals"),
              })}
            />
          </Dropdown>
        );
      }
      case FIELD_UID_ASSESSMENT_TOOL:
        return (
          <CustomDropdown
            options={options}
            assessmentTools={_.get(assessmentTools, "edges", [])}
            curriculumProgramType={curriculumType}
            onChangeDropDownMenuOption={option =>
              handleAssessmentToolDropdownClick(option)
            }
            parentType={RESOURCE_TYPE_QUESTION}
            assessmentId={assessmentId}
          />
        );
      default:
        return null;
    }
  };

  const dynamicFields = isDynamicFieldDropdown
    ? [FIELD_UID_ADD_LEARNING_GOALS, FIELD_UID_ASSESSMENT_TOOL]
    : DYNAMIC_FIELDS_V2;

  return _.map(dynamicFields, fieldUid => (
    <DynamicFieldComponent
      fieldUid={fieldUid}
      getExtraRightButtonComponent={getExtraRightButtonComponent}
      data={data}
      updateFields={updateFields}
      updateInputField={updateInputField}
      isActiveQuestion={isActiveQuestion}
      activeQuestionField={activeQuestionField}
      questionId={questionId}
      assessmentTools={assessmentTools}
      removeAssessmentTool={removeAssessmentTool}
      mode={mode}
      didMount={didMount}
      setDidMount={setDidMount}
      showDynamicFields={showDynamicFields}
      isSubQuestionSelected={isSubQuestionSelected}
      updateActiveQuestionField={updateActiveQuestionField}
      curriculumType={curriculumType}
      isFromInternalTool={isFromInternalTool}
      portalType={portalType}
      learningGoalsDropdownOptions={learningGoalsDropdownOptions}
      selectedCourseId={selectedCourseId}
      learningGoalsFields={learningGoalsFields}
      assessmentType={assessmentType}
      worksheetTemplateId={worksheetTemplateId}
      unitPlanFields={unitPlanFields}
      unitPlanFieldList={unitPlanFieldList}
      createQuestionField={createQuestionField}
      learningGoalsMapping={learningGoalsMapping}
      getLearningGoalsTagCountTooltip={getLearningGoalsTagCountTooltip}
      dynamicFieldDetails={dynamicFieldDetails}
      setDynamicFieldRef={setDynamicFieldRef}
      addedLearningGoalsFieldsUid={addedLearningGoalsFieldsUid}
      addedLearningGoalsFields={addedLearningGoalsFields}
      fieldRefs={fieldRefs}
      isDynamicFieldDropdown={isDynamicFieldDropdown}
      t={t}
      fieldList={fieldList}
      assessmentFields={assessmentFields}
      isSubQuestion={isSubQuestion}
      onOptionClick={addNewQuestionField}
      unitPlanTemplate={unitPlanTemplate}
    />
  ));
};

const mapStateToProps = (state, ownProps) => {
  const { data, questionFields } = ownProps;

  const activeQuestionField = _.get(state, "worksheet.activeQuestionField");
  const activeTab = _.get(state, "login.activeTab", "");
  const isToddleLibraryPortal = activeTab == TODDLE_LIBRARY;

  const curriculumType = isToddleLibraryPortal
    ? _.get(state, "classRoom.curriculumProgram.type", "")
    : _.get(state, "platform.currentCurriculumProgram.type", "");

  const assessmentId = _.get(
    state,
    "assessment.assessmentData.assessmentId",
    ""
  );

  const academicYearId = getSelectedAcademicYearId(state);
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const {
    assessmentType,
    worksheetTemplateId,
    unitPlanTemplateId,
    assessmentDetails,
    unitPlanField,
    subjectsField,
    assessmentFields,
  } = getAssessmentDetailsData({ assessmentId, collaboratorsFilter });

  const { unitPlanFields } = getUnitDetailsFromUnitPlanField({
    assessmentDetails,
  });

  const worksheetTemplateCacheData = getPlannerTemplateFromCache(
    { ids: [worksheetTemplateId] },
    false
  );

  const worksheetTemplate = _.get(worksheetTemplateCacheData, "body", {});

  /** unit plan & subjects field needed for show unit/subjects based data in learning goals */
  const modifiedData = [...data, unitPlanField, subjectsField];

  const unitPlanTemplateCacheData = getPlannerTemplateFromCache(
    { ids: [unitPlanTemplateId] },
    false
  );
  const unitPlanTemplate = _.get(unitPlanTemplateCacheData, "body", {});

  const updatedAssessmentFields = replaceLearningGoalsFields({
    template: worksheetTemplate,
    fields: assessmentFields,
    replacementFields: questionFields,
  });

  return {
    curriculumType,
    activeQuestionField,
    selectedCourseId: getSelectedCourseId(state),
    assessmentType,
    worksheetTemplateId,
    unitPlanFields: combineUnitPlanMultiFieldData({
      templateBody: worksheetTemplate,
      unitPlanFields: _.cloneDeep(unitPlanFields),
    }),
    unitPlanFieldList: _.get(unitPlanTemplate, "field_list", {}),
    data: modifiedData,
    assessmentId,
    assessmentFields: updatedAssessmentFields,
    questionFields,
  };
};

const mapActionCreators = {
  removeAssessmentTool,
  updateActiveQuestionField,
  createQuestionField,
};

DynamicFieldsViewV2.propTypes = {
  data: PropTypes.array,
  updateFields: PropTypes.func,
  isActiveQuestion: PropTypes.bool,
  showDynamicFields: PropTypes.bool,
  isSubQuestionSelected: PropTypes.bool,
  isFromInternalTool: PropTypes.bool,
  learningGoalsDropdownOptions: PropTypes.arrayOf(PropTypes.object),
  learningGoalsFields: PropTypes.objectOf(PropTypes.object),
  assessmentId: PropTypes.string,
  isDynamicFieldDropdown: PropTypes.bool,
  fieldList: PropTypes.object,
  questionFields: PropTypes.arrayOf(PropTypes.object),
  isSubQuestion: PropTypes.bool,
  onOptionClick: PropTypes.func,
};

DynamicFieldsViewV2.defaultProps = {
  showDynamicFields: false,
  learningGoalsDropdownOptions: [],
  learningGoalsFields: {},
  isDynamicFieldDropdown: false,
  fieldList: {},
  questionFields: [],
  onOptionClick: _.noop,
};

export default connect(mapStateToProps, mapActionCreators)(DynamicFieldsViewV2);
